import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { DataGrid } from '@mui/x-data-grid';
import Modal from '../../components/Modal';
import StoreService from '../../components/StoreService';
import UserService from '../../components/UserService';
import useAuth from '../../components/useAuth';

function StoresView() {
  const { roles } = useAuth();
  const isAdminOrManager = roles.includes('admin') || roles.includes('manager');
  
  const empty = {
    name: '',
    storeCode: '',
    city: '',
    store_type: '',
    is_active: true,
    entrytime: '10:00',
    outtime: '22:00',
  };
  const [stores, setStores] = useState([]);
  const [formData, setFormData] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');

  const fetchData = async () => {
    const data = await StoreService.fetchStores();
    setStores(data.response || data);
  };

  const fetchUsers = async () => {
    const data = await UserService.fetchUsers();
    const usersList = Array.isArray(data) ? data : (data.response || []);
    setUsers(usersList);
  };

  useEffect(() => { 
    fetchData();
    fetchUsers();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    const payload = {
      name: formData.name,
      storeCode: formData.storeCode,
      city: formData.city,
      store_type: formData.store_type,
      is_active: Boolean(formData.is_active),
      timings: { entrytime: formData.entrytime, outtime: formData.outtime },
    };
    if (editingId) await StoreService.updateStore(editingId, payload);
    else await StoreService.createStore(payload);
    setFormData(empty);
    setEditingId(null);
    fetchData();
  };

  const handleEdit = (store) => {
    setEditingId(store._id || store.id);
    setFormData({
      name: store.name,
      storeCode: store.storeCode,
      city: store.city,
      store_type: store.store_type,
      is_active: store.is_active !== false,
      entrytime: store.timings?.entrytime || '10:00',
      outtime: store.timings?.outtime || '22:00',
    });
  };

  const handleActiveChange = (e) => {
    setFormData({ ...formData, is_active: e.target.checked });
  };

  const handleToggleActive = async (store) => {
    const id = store._id || store.id;
    if (!id) return;

    await StoreService.updateStore(id, {
      is_active: store.is_active === false,
    });
    fetchData();
  };

  const handleDelete = async (id) => {
    await StoreService.deleteStore(id);
    fetchData();
  };

  const handleManageProcessors = (store) => {
    setSelectedStore(store);
    setSelectedUserId('');
  };

  const handleAddProcessor = async () => {
    if (!selectedUserId || !selectedStore) return;
    try {
      const updatedStore = await StoreService.addProcessorToStore(selectedStore._id || selectedStore.id, selectedUserId);
      await fetchData();
      setSelectedStore(updatedStore); // Update modal view immediately
      setSelectedUserId('');
    } catch (error) {
      console.error('Error adding processor:', error);
      alert('Erreur lors de l\'ajout du processeur');
    }
  };

  const getUserOptions = () => {
    const currentProcessors = selectedStore ? getStoreProcessors(selectedStore) : [];
    return users
      .filter(user => !currentProcessors.includes(user._id || user.id))
      .map(user => ({
        value: user._id || user.id,
        label: `${user.name || ''} ${user.lastname || ''}`.trim() + ` (${user.username})`
      }));
  };

  const handleRemoveProcessor = async (userId) => {
    if (!selectedStore) return;
    try {
      const updatedStore = await StoreService.removeProcessorFromStore(selectedStore._id || selectedStore.id, userId);
      await fetchData();
      setSelectedStore(updatedStore); // Update modal view immediately
    } catch (error) {
      console.error('Error removing processor:', error);
      alert('Erreur lors de la suppression du processeur');
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => (u._id || u.id) === userId);
    return user ? `${user.name || ''} ${user.lastname || ''}`.trim() || user.username || userId : userId;
  };

  const getStoreProcessors = (store) => {
    const processors = store.processors || [];
    // Handle both formats: array of strings or array of objects with user_id
    return processors.map(p => typeof p === 'string' ? p : p.user_id);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Nom',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div className="fw-semibold">{params.value}</div>
      ),
    },
    {
      field: 'storeCode',
      headerName: 'Code',
      width: 130,
      renderCell: (params) => (
        <span className="badge bg-secondary">{params.value}</span>
      ),
    },
    {
      field: 'city',
      headerName: 'Ville',
      width: 150,
      renderCell: (params) => (
        <div>
          <i className="bi bi-geo-alt me-2 text-muted"></i>
          {params.value}
        </div>
      ),
    },
    {
      field: 'store_type',
      headerName: 'Type',
      width: 130,
      renderCell: (params) => (
        <span className="badge bg-info text-dark">{params.value}</span>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Statut',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isActive = params.row.is_active !== false;
        return (
          <div className="d-flex flex-column align-items-center justify-content-center gap-1 w-100 py-1">
            <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
              {isActive ? 'Actif' : 'Inactif'}
            </span>
            <div className="form-check form-switch m-0 p-0 d-flex justify-content-center">
              <input
                className="form-check-input m-0"
                type="checkbox"
                role="switch"
                checked={isActive}
                onClick={(event) => event.stopPropagation()}
                onChange={() => handleToggleActive(params.row)}
                title={isActive ? 'Désactiver ce store' : 'Activer ce store'}
              />
            </div>
          </div>
        );
      },
    },
    {
      field: 'timings',
      headerName: 'Horaires',
      width: 180,
      renderCell: (params) => (
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-clock me-1 text-muted"></i>
          <small>{params.value?.entrytime} - {params.value?.outtime}</small>
        </div>
      ),
    },
    ...(isAdminOrManager ? [{
      field: 'processors',
      headerName: 'Processeurs',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <button
          className="btn btn-sm btn-outline-primary rounded-pill"
          data-bs-toggle="modal"
          data-bs-target="#processorsModal"
          onClick={() => handleManageProcessors(params.row)}
        >
          <i className="bi bi-people me-1"></i>
          {getStoreProcessors(params.row).length}
        </button>
      ),
    }] : []),
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => (
        <div className="btn-group" role="group">
          <button
            className="btn btn-sm btn-warning"
            data-bs-toggle="modal"
            data-bs-target="#storeModal"
            onClick={() => handleEdit(params.row)}
            title="Modifier"
          >
            Modifier
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleDelete(params.row._id || params.row.id)}
            title="Supprimer"
          >
            Supprimer
          </button>
        </div>
      ),
    },
  ];

  const rows = stores.map(store => ({
    ...store,
    id: store._id || store.id,
  }));

  return (
    <>
      <div className="container-fluid mt-4 px-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1 fw-bold">Gestion des magasins</h2>
          <p className="text-muted mb-0">Gérez vos magasins et leurs processeurs</p>
        </div>
        <button
          className="btn btn-success shadow-sm px-4"
          data-bs-toggle="modal"
          data-bs-target="#storeModal"
          onClick={() => {
            setFormData(empty);
            setEditingId(null);
          }}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Nouveau magasin
        </button>
      </div>
      
      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            checkboxSelection={false}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f8f9fa',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid #dee2e6',
                backgroundColor: '#f8f9fa',
              },
            }}
          />
        </div>
      </div>
      </div>

      <Modal modalId="storeModal" onSubmit={handleSubmit} submitLabel={editingId ? 'Modifier' : 'Ajouter'} modalType="modal-lg">
        <form>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Nom du magasin</label>
              <input 
                className="form-control form-control-lg" 
                name="name" 
                value={formData.name} 
                onChange={handleChange} 
                placeholder="Nom du magasin" 
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Code magasin</label>
              <input 
                className="form-control form-control-lg" 
                name="storeCode" 
                value={formData.storeCode} 
                onChange={handleChange} 
                placeholder="Code" 
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Ville</label>
              <input 
                className="form-control form-control-lg" 
                name="city" 
                value={formData.city} 
                onChange={handleChange} 
                placeholder="Ville" 
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Type de magasin</label>
              <input 
                className="form-control form-control-lg" 
                name="store_type" 
                value={formData.store_type} 
                onChange={handleChange} 
                placeholder="Type" 
              />
            </div>
            <div className="col-md-6 d-flex align-items-center">
              <div className="form-check form-switch mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="storeIsActive"
                  checked={formData.is_active !== false}
                  onChange={handleActiveChange}
                />
                <label className="form-check-label fw-semibold" htmlFor="storeIsActive">
                  Store actif
                </label>
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-clock me-2"></i>Heure d'entrée
              </label>
              <input 
                className="form-control form-control-lg" 
                name="entrytime" 
                type="time" 
                value={formData.entrytime} 
                onChange={handleChange} 
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-clock me-2"></i>Heure de sortie
              </label>
              <input 
                className="form-control form-control-lg" 
                name="outtime" 
                type="time" 
                value={formData.outtime} 
                onChange={handleChange} 
              />
            </div>
          </div>
        </form>
      </Modal>

      {isAdminOrManager && (
        <Modal modalId="processorsModal" submitLabel="Fermer" onSubmit={() => setSelectedStore(null)} modalType="modal-lg">
          <div>
            <div className="mb-4 pb-3 border-bottom">
              <h4 className="mb-1 fw-bold">
                <i className="bi bi-shop me-2 text-primary"></i>
                {selectedStore?.name}
              </h4>
              <p className="text-muted mb-0">Gestion des Processeurs</p>
            </div>
            
            <div className="mb-4 p-3 bg-light rounded">
              <label className="form-label fw-semibold mb-3">
                <i className="bi bi-person-plus me-2"></i>
                Ajouter un processeur
              </label>
              <div className="d-flex gap-2">
                <div style={{ flex: 1 }}>
                  <Select
                    options={getUserOptions()}
                    value={getUserOptions().find(opt => opt.value === selectedUserId) || null}
                    onChange={(option) => setSelectedUserId(option ? option.value : '')}
                    placeholder="Rechercher un utilisateur..."
                    isClearable
                    isSearchable
                    noOptionsMessage={() => 'Aucun utilisateur trouvé'}
                  />
                </div>
                <button 
                  className="btn btn-primary px-4" 
                  onClick={handleAddProcessor}
                  disabled={!selectedUserId}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Ajouter
                </button>
              </div>
            </div>

            <div className="mt-4">
              <h6 className="fw-semibold mb-3">
                <i className="bi bi-people me-2 text-primary"></i>
                Processeurs actuels ({selectedStore ? getStoreProcessors(selectedStore).length : 0})
              </h6>
              {selectedStore && getStoreProcessors(selectedStore).length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-inbox display-1 text-muted d-block mb-3"></i>
                  <p className="text-muted">Aucun processeur assigné à ce magasin</p>
                </div>
              ) : (
                <div className="list-group shadow-sm">
                  {selectedStore && getStoreProcessors(selectedStore).map((userId, index) => (
                    <div key={userId || index} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                          <i className="bi bi-person text-primary"></i>
                        </div>
                        <span className="fw-semibold">{getUserName(userId)}</span>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-danger rounded-pill"
                        onClick={() => handleRemoveProcessor(userId)}
                      >
                        <i className="bi bi-x-circle me-1"></i>
                        Retirer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default StoresView;
